/**
 * @file Payment Account Mapper Utility
 * Translates between the flat API response and the internal InternalPaymentMethod
 * shape, and builds POST/PATCH payloads with correct field casing per the API contract.
 */

/**
 * Parse a card expiration date into "MM/YYYY".
 * Handles "MM/YYYY", "M/YYYY", and ISO-like "YYYY-MM..." inputs.
 * @param {string|null|undefined} dateStr
 * @returns {string|null}
 */
function formatExpirationDate(dateStr) {
  if (!dateStr) return null;

  // ISO-like: YYYY-MM(-DD)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})/);
  if (isoMatch) {
    return `${isoMatch[2].padStart(2, '0')}/${isoMatch[1]}`;
  }

  // Any M/YYYY or MM/YYYY
  const slashMatch = dateStr.match(/(\d{1,2})\/(\d{4})/);
  if (slashMatch) {
    return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2]}`;
  }

  return null;
}

/**
 * Parse "MM/YYYY" into { month, year } integers.
 * @param {string} dateStr
 * @returns {{ month: number, year: number }}
 */
function parseExpirationDate(dateStr) {
  const [month, year] = dateStr.split('/');
  return { month: parseInt(month, 10), year: parseInt(year, 10) };
}

/**
 * Normalize a single flat API response object into the internal format.
 * @param {Object} apiItem
 * @returns {Object|null}
 */
export function normalizePaymentAccount(apiItem) {
  if (!apiItem || !apiItem.accountId) return null;

  const isBankAccount = apiItem.category === 'bankaccount';

  return {
    accountName: isBankAccount ? (apiItem.bankName || '') : (apiItem.network || ''),
    accountNumber: isBankAccount ? (apiItem.accountNumberLast4 || '') : (apiItem.cardNumberLast4 || ''),
    routingNumber: apiItem.routingNumber || undefined,
    accountholderName: apiItem.accountHolderName || '',
    paymentAccountId: apiItem.accountId,
    type: isBankAccount
      ? ['paymentAccount', 'accountType', apiItem.accountType || '']
      : (apiItem.accountType || ''),
    isPrimary: apiItem.isPrimary || false,
    brand: apiItem.network || undefined,
    address: {
      address: apiItem.address || '',
      city: apiItem.city || '',
      state: apiItem.state || '',
      zipCode: apiItem.zipcode || '',
    },
    cardExpiration: formatExpirationDate(apiItem.cardExpirationDate),
    category: apiItem.category || undefined,
  };
}

/**
 * Normalize an array of flat API response objects, dropping invalid items.
 * @param {Object[]} apiItems
 * @returns {Object[]}
 */
export function normalizePaymentAccounts(apiItems) {
  if (!Array.isArray(apiItems)) return [];
  return apiItems.map(normalizePaymentAccount).filter(Boolean);
}

/**
 * Build POST payload. Bank: `accountholderName` (lowercase h). Card: `cardHolderName`.
 * Uses `zipcode` (lowercase c).
 * @param {Object} formValues
 * @param {'bankaccount'|'bankcard'} category
 * @returns {Object}
 */
export function buildPostPayload(formValues, category) {
  if (!formValues) throw new Error('buildPostPayload: formValues is required');

  const basePayload = {
    address: formValues.address || '',
    city: formValues.city || '',
    state: formValues.state || '',
    zipcode: formValues.zipcode || '',
    country: 'USA',
  };

  if (category === 'bankaccount') {
    return {
      ...basePayload,
      accountholderName: (formValues.accountholderName || '').trim(),
      accountType: formValues.accountType || '',
      accountNumber: formValues.accountNumber || '',
      routingNumber: formValues.routingNumber || '',
      bankName: formValues.bankName || '',
    };
  }

  return {
    ...basePayload,
    accountType: formValues.accountType || 'debit',
    cardHolderName: (formValues.cardHolderName || '').trim(),
    cardNumber: formValues.cardNumber || '',
    expirationDate: parseExpirationDate(formValues.expirationDate),
    network: formValues.network || '',
  };
}

/**
 * Build PATCH payload. Bank: `accountHolderName` (capital H). Card: `cardHolderName`.
 * Uses `zipCode` (capital C) and `paymentAccountId` (from accountId).
 * @param {Object} formValues
 * @param {'bankaccount'|'bankcard'} category
 * @param {string} accountId
 * @returns {Object}
 */
export function buildPutPayload(formValues, category, accountId) {
  if (!formValues) throw new Error('buildPutPayload: formValues is required');
  if (!accountId) throw new Error('buildPutPayload: accountId is required');

  const basePayload = {
    paymentAccountId: accountId,
    address: formValues.address || '',
    city: formValues.city || '',
    state: formValues.state || '',
    zipCode: formValues.zipcode || '',
    country: 'USA',
  };

  if (category === 'bankaccount') {
    return {
      ...basePayload,
      accountHolderName: (formValues.accountholderName || '').trim(),
    };
  }

  const result = {
    ...basePayload,
    cardHolderName: (formValues.cardHolderName || '').trim(),
  };
  if (formValues.expirationDate) {
    result.expirationDate = parseExpirationDate(formValues.expirationDate);
  }
  return result;
}

/**
 * Primary payment method identifier "{accountId}-{accountType}" or "".
 * @param {Object[]} apiItems
 * @returns {string}
 */
export function derivePrimaryPaymentMethod(apiItems) {
  if (!Array.isArray(apiItems)) return '';
  const primary = apiItems.find((item) => item && item.isPrimary);
  if (!primary || !primary.accountId) return '';
  return `${primary.accountId}-${(primary.accountType || '').toLowerCase()}`;
}
